<script>
  import { onMount } from "svelte";

  import DashboardLayout from "../../layouts/Dashboard.svelte";
  import LogoText from "../../components/LogoText.svelte";

  import TotalFollowings from "../../app/dashboard/TotalFollowings.svelte";
  import TotalFollowers from "../../app/dashboard/TotalFollowers.svelte";
  import TotalEngagement from "../../app/dashboard/TotalEngagement.svelte";
  import BlockSocialProfiles from "../../app/dashboard/BlockSocialProfiles.svelte";
  import RecentActivity from "../../app/recent_activity/RecentActivity.svelte";
  import PlatformStats from "../../app/dashboard/PlatformStats.svelte";

  import { makeApiRequest } from "../../utils/ajax";
  import AddSocialForm from "../../app/dashboard/AddSocialForm.svelte";

  let props = $props();

  let loadingSocialProfiles = $state(false);
  let showAddProfileForm = $state(false);
  let socialProfiles = $state([]);

  let totalFollowings = $derived(
    socialProfiles.reduce((acc, sp) => acc + sp.profile.following_count, 0)
  );

  let totalFollowers = $derived(
    socialProfiles.reduce((acc, sp) => acc + sp.profile.followers_count, 0)
  );

  let totalEngagement = $derived(
    Math.round(
      socialProfiles.reduce((acc, sp) => acc + sp.profile.engagement_score, 0) /
        socialProfiles.length
    )
  );

  function formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num;
  }

  let loadSocialProfiles = async () => {
    loadingSocialProfiles = true;
    socialProfiles = await makeApiRequest("/api/social_profiles");
    loadingSocialProfiles = false;
  };

  onMount(() => {
    loadSocialProfiles();
  });
</script>

<DashboardLayout {...props} container={false}>
  {#snippet content()}
    {#if loadingSocialProfiles}
      {@render showLoading()}
    {:else if socialProfiles.length > 0}
      {@render showSocialProfiles()}
    {:else}
      {@render noContentAdded()}
    {/if}
  {/snippet}
</DashboardLayout>

{#snippet showSocialProfiles()}
  <div class="py-4 sm:py-8 px-4 sm:px-6 lg:px-8">
    <div class="max-w-7xl mx-auto">
      <div class="mb-6 sm:mb-8">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p class="mt-2 text-gray-600 text-sm sm:text-base">
          Track your social media growth and performance across platforms.
        </p>
      </div>

      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8"
      >
        <TotalFollowings {totalFollowings} {formatNumber} />
        <TotalFollowers {totalFollowers} {formatNumber} />
        <TotalEngagement {totalEngagement} />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div
          class="lg:col-span-2 bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-100 h-full"
        >
          <BlockSocialProfiles {loadSocialProfiles} {socialProfiles} />
        </div>
        <div
          class="lg:col-span-1 bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-100 h-full"
        >
          <RecentActivity />
        </div>
      </div>

      <div class="mb-6 sm:mb-8">
        <PlatformStats />
      </div>
    </div>
  </div>
{/snippet}

{#snippet noContentAdded()}
  <div
    class="flex flex-col items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8 min-h-[60vh] bg-gradient-to-br from-indigo-50 to-white"
  >
    <div class="w-full max-w-6xl mx-auto">
      <div
        class="flex flex-col lg:flex-row lg:space-x-12 space-y-6 sm:space-y-8 lg:space-y-0"
      >
        <div
          class="w-full lg:w-1/2 bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-indigo-100 transform transition-all duration-300 hover:shadow-xl"
        >
          <div class="text-center">
            <div
              class="bg-indigo-100 p-3 sm:p-4 rounded-full inline-block mb-2"
            >
              <svg
                class="h-12 sm:h-16 w-12 sm:w-16 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                ></path>
              </svg>
            </div>
            <h2
              class="mt-4 text-2xl sm:text-3xl font-bold text-gray-900 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600"
            >
              Get started with <LogoText size="md" color="indigo" />
            </h2>
            <p class="mt-3 text-sm sm:text-base text-gray-600 max-w-md mx-auto">
              Add your first social media profile to start tracking your growth
              and unlock powerful analytics and insights.
            </p>
          </div>

          <div class="mt-6 sm:mt-8">
            <AddSocialForm
              {loadSocialProfiles}
              title="Connect your first profile"
            />
          </div>
        </div>

        <div class="w-full lg:w-1/2 flex items-center">
          <div class="w-full">
            <h3
              class="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center lg:text-left"
            >
              Why track your social media?
            </h3>
            <div class="grid grid-cols-1 gap-4 sm:gap-5">
              <div
                class="bg-white p-4 sm:p-6 rounded-xl shadow-md border-l-4 border-pink-500 transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div class="flex items-center mb-2">
                  <div class="p-2 bg-pink-100 rounded-lg mr-3">
                    <svg
                      class="w-4 sm:w-5 h-4 sm:h-5 text-pink-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-gray-900 font-bold text-base sm:text-lg"
                    >Growth Insights</span
                  >
                </div>
                <p class="mt-2 text-gray-600 text-sm sm:text-base">
                  Track followers and engagement over time to understand your
                  audience growth patterns and identify what drives new
                  followers.
                </p>
              </div>

              <div
                class="bg-white p-4 sm:p-6 rounded-xl shadow-md border-l-4 border-blue-500 transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div class="flex items-center mb-2">
                  <div class="p-2 bg-blue-100 rounded-lg mr-3">
                    <svg
                      class="w-4 sm:w-5 h-4 sm:h-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-gray-900 font-bold text-base sm:text-lg"
                    >Content Analysis</span
                  >
                </div>
                <p class="mt-2 text-gray-600 text-sm sm:text-base">
                  See which posts perform best and understand the content types
                  that resonate most with your audience.
                </p>
              </div>

              <div
                class="bg-white p-4 sm:p-6 rounded-xl shadow-md border-l-4 border-green-500 transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div class="flex items-center mb-2">
                  <div class="p-2 bg-green-100 rounded-lg mr-3">
                    <svg
                      class="w-4 sm:w-5 h-4 sm:h-5 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-gray-900 font-bold text-base sm:text-lg"
                    >Performance Reports</span
                  >
                </div>
                <p class="mt-2 text-gray-600 text-sm sm:text-base">
                  Get weekly insights delivered directly to your inbox with
                  actionable recommendations to improve your social media
                  presence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/snippet}

{#snippet showLoading()}
  <div
    class="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="w-full max-w-md mx-auto text-center">
      <div class="animate-pulse">
        <svg
          class="mx-auto h-16 w-16 text-indigo-400"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="2"
            opacity="0.7"
          ></circle>
          <path
            class="opacity-0.9"
            d="M12 6v6l4 2"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></path>
        </svg>
      </div>
      <h2 class="mt-6 text-2xl font-semibold text-gray-700">
        Loading your profiles
      </h2>
      <p class="mt-2 text-gray-500">
        Please wait while we fetch your social media data...
      </p>
      <div class="mt-6 flex justify-center">
        <div class="w-16 h-1.5 bg-indigo-200 rounded-lg overflow-hidden">
          <div
            class="h-full bg-indigo-500 animate-[loadingBar_1.5s_ease-in-out_infinite]"
          ></div>
        </div>
      </div>
    </div>
  </div>
{/snippet}
